import { isEmpty } from 'lodash-es';
import { forwardRef } from 'react';

import { layoutContainer } from './GaugeChartLayout.sty.js';
import type { ComponentWithName } from '../../../core/types/component-with-name.js';
import { ARC_DEFAULT_VALUE_COLOR } from '../../constants.js';
import { useGaugeChartRangeMaxContext } from '../../hooks/useGaugeChartRangeMaxContext.js';
import { useGaugeChartRangeMinContext } from '../../hooks/useGaugeChartRangeMinContext.js';
import { useGaugeChartThresholdContext } from '../../hooks/useGaugeChartThresholdContext.js';
import { findMatchingThreshold } from '../../utils/find-matching-thresholds.js';
import { GaugeBaseArc } from '../GaugeBaseArc.js';
import { GaugeLabels } from '../GaugeLabels.js';
import { GaugeRootSVG } from '../GaugeRootSVG.js';
import { GaugeThresholdIndicators } from '../GaugeThresholdIndicators.js';
import { GaugeValueArc } from '../GaugeValueArc.js';

interface GaugeChartLayoutProps {
  value: number;
  color: string;
}

export const GaugeChartLayout = forwardRef<
  HTMLDivElement,
  GaugeChartLayoutProps
>(({ value, color }, forwardedRef) => {
  const thresholds = useGaugeChartThresholdContext();
  const min = useGaugeChartRangeMinContext();
  const max = useGaugeChartRangeMaxContext();

  const matchingThreshold =
    thresholds && findMatchingThreshold(value, thresholds);

  const valueArcColor =
    matchingThreshold?.color ?? color ?? ARC_DEFAULT_VALUE_COLOR;

  return (
    <div
      data-testid="gauge-layout-container"
      ref={forwardedRef}
      className={layoutContainer}
    >
      <GaugeRootSVG>
        <GaugeBaseArc />
        <GaugeValueArc
          value={value}
          color={valueArcColor}
          min={min}
          max={max}
        />
        {!isEmpty(thresholds) && <GaugeThresholdIndicators />}
        <GaugeLabels value={value} />
      </GaugeRootSVG>
    </div>
  );
});

(GaugeChartLayout as ComponentWithName<typeof GaugeChartLayout>).displayName =
  'GaugeChartLayout';
